import { useParams, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useArticle, extractArticleTitle, extractArticleSummary, extractArticleImage, extractArticlePublishedAt, extractArticleTags } from '@/hooks/useArticles';
import { useAuthor } from '@/hooks/useAuthor';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Calendar, User } from 'lucide-react';
import { genUserName } from '@/lib/genUserName';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MY_PUBKEY = 'a8d1560d6a647d501699167246f237b36fb123f89168fda11dc743533fec7a08';

const ArticlePage = () => {
  const { dTag } = useParams<{ dTag: string }>();
  const { data: article, isLoading } = useArticle(MY_PUBKEY, dTag);
  const author = useAuthor(MY_PUBKEY);
  const metadata = author.data?.metadata;

  const title = article ? extractArticleTitle(article) : null;
  const summary = article ? extractArticleSummary(article) : null;
  const image = article ? extractArticleImage(article) : null;
  const publishedAt = article ? extractArticlePublishedAt(article) : null;
  const tags = article ? extractArticleTags(article) : [];

  const displayName = metadata?.name || genUserName(MY_PUBKEY);
  const avatarUrl = metadata?.picture;

  const timestamp = article ? (publishedAt || article.created_at) : 0;
  const dateStr = timestamp
    ? new Date(timestamp * 1000).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  useSeoMeta({
    title: title || 'Article',
    description: summary || 'Read this article on stl1988\'s homepage',
    ogTitle: title || 'Article',
    ogDescription: summary || 'Read this article on stl1988\'s homepage',
    ogImage: image || 'https://stl1988.shakespeare.wtf/og-image.jpg',
    ogType: 'article',
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container py-8 px-4 max-w-4xl">
          <Button asChild variant="ghost" size="sm" className="mb-6">
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Link>
          </Button>

          <Card>
            <CardHeader className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <div className="flex items-center gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container py-8 px-4 max-w-4xl">
          <Button asChild variant="ghost" size="sm" className="mb-6">
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Link>
          </Button>

          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Article not found</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container py-8 px-4 max-w-4xl">
        <Button asChild variant="ghost" size="sm" className="mb-6">
          <Link to="/">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
        </Button>

        <article>
          <Card>
            <CardHeader className="space-y-6">
              <div className="space-y-4">
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
                  {title || 'Untitled Article'}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={avatarUrl} alt={displayName} />
                      <AvatarFallback>{displayName[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex items-center gap-1.5">
                      <User className="h-3 w-3" />
                      <span>{displayName}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3 w-3" />
                    <span>{dateStr}</span>
                  </div>
                </div>

                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag, index) => (
                      <Badge key={index} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {image && (
                <div className="rounded-lg overflow-hidden">
                  <img
                    src={image}
                    alt={title || 'Article image'}
                    className="w-full h-auto"
                  />
                </div>
              )}
            </CardHeader>

            <CardContent>
              <div className="prose prose-sm sm:prose-base dark:prose-invert max-w-none prose-headings:font-heading prose-a:text-primary hover:prose-a:underline prose-img:rounded-lg prose-pre:bg-muted prose-pre:border">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {article.content}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </article>
      </div>
    </div>
  );
};

export default ArticlePage;
